
import { CheckCircle, XCircle, Clock, Mail } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { User } from '@/types/user';

interface UserStatusDisplayProps {
  user: User;
}

export function UserStatusDisplay({ user }: UserStatusDisplayProps) {
  const getStatusIcon = () => {
    // If user is inactive, show inactive icon regardless of invitation status
    if (user.status === 'Inactive') {
      return <XCircle className="mr-2 h-4 w-4 text-gray-400" />;
    }
    
    // If invitation is still pending (user hasn't signed in yet)
    if (user.invitationStatus === 'pending') {
      return <Clock className="mr-2 h-4 w-4 text-yellow-500" />;
    }
    
    // User is active and has accepted invitation
    return <CheckCircle className="mr-2 h-4 w-4 text-green-500" />;
  };

  const getStatusText = () => {
    // If user account is inactive, show that first
    if (user.status === 'Inactive') {
      return 'Inactive';
    }
    
    // If invitation is still pending (user hasn't signed in yet)
    if (user.invitationStatus === 'pending') {
      return 'Invitation Pending';
    }
    
    // User has accepted invitation and is active
    return 'Active';
  };

  const getStatusBadgeVariant = () => {
    // If user account is inactive
    if (user.status === 'Inactive') {
      return 'outline' as const;
    }
    
    // If invitation is still pending
    if (user.invitationStatus === 'pending') {
      return 'secondary' as const;
    }
    
    // User is active
    return 'default' as const;
  };

  return (
    <div className="flex items-center">
      {getStatusIcon()}
      <Badge variant={getStatusBadgeVariant()}>
        {getStatusText()}
      </Badge>
      {user.invitationStatus === 'pending' && (
        <Mail className="ml-2 h-3 w-3 text-muted-foreground" />
      )}
    </div>
  );
}
